/**
 * Camera slug lookup — maps URL-friendly slugs to camera data.
 * Used by /camera/:slug pages for programmatic SEO.
 *
 * Slug format: {brand}-{model} e.g. "dahua-hfw3849t1-as", "hikvision-ds-2cd2387g2"
 */

import { CAMERA_MANUFACTURERS } from '@/data/cameraManufacturers';
import type { CameraModel } from '@/components/cctv-designer-v2/types';

export interface CameraEntry {
  manufacturer: string;
  model: CameraModel;
  slug: string;
}

/** Convert a camera name to a URL-safe slug */
function toSlug(manufacturer: string, modelName: string): string {
  return `${manufacturer}-${modelName}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

// Filter out filler models (Extra000, MEGA_EXPANSION patterns)
function isRealModel(name: string): boolean {
  if (/Extra\d{3}$/.test(name)) return false;
  if (/^[A-Z]+-GEN\d+/.test(name)) return false;
  return true;
}

// Build lookup maps lazily (only once, on first access)
let _entries: CameraEntry[] | null = null;
let _bySlug: Map<string, CameraEntry> | null = null;

function buildLookup() {
  if (_entries) return;

  _entries = [];
  _bySlug = new Map();

  for (const mfg of CAMERA_MANUFACTURERS) {
    if (mfg.name === 'Generic') continue;

    for (const model of mfg.models) {
      if (!isRealModel(model.name)) continue;

      const slug = toSlug(mfg.name, model.name);

      // Skip duplicates (first wins)
      if (_bySlug.has(slug)) continue;

      const entry: CameraEntry = {
        manufacturer: mfg.name,
        model,
        slug,
      };

      _entries.push(entry);
      _bySlug.set(slug, entry);
    }
  }
}

/** Get camera data by slug, or undefined if not found */
export function getCameraBySlug(slug: string): CameraEntry | undefined {
  buildLookup();
  return _bySlug!.get(slug);
}

/** Get all camera entries (for sitemap, index page, etc.) */
export function getAllCameras(): CameraEntry[] {
  buildLookup();
  return _entries!;
}

/** Get all unique manufacturer names (sorted) */
export function getAllManufacturers(): string[] {
  buildLookup();
  const set = new Set(_entries!.map(e => e.manufacturer));
  return Array.from(set).sort();
}

/** Get cameras by manufacturer */
export function getCamerasByManufacturer(manufacturer: string): CameraEntry[] {
  buildLookup();
  return _entries!.filter(e => e.manufacturer.toLowerCase() === manufacturer.toLowerCase());
}

/** Generate the slug for a given manufacturer + model name */
export { toSlug as cameraSlug };
